import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguageContext } from '../navigations/homenav';

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

export default function ForgotPassword() {
  const { t } = useLanguageContext();
  const [step, setStep] = useState('request'); // request | verify
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage(t('fpEnterEmail'));
      return;
    }
    setIsLoading(true);
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE_URL}/request_password_reset.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await resp.json();
      // Always move to next step to avoid user enumeration
      if (resp.ok && data.status === 'success') {
        toast.success(t('fpIfAccountExists'));
      } else {
        // Still generic success to prevent enumeration
        toast.success(t('fpIfAccountExists'));
      }
      setStep('verify');
    } catch (err) {
      console.error('Reset request error:', err);
      toast.error(t('fpRequestFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setMessage(t('fpEnterCode'));
      return;
    }
    if (!newPassword.trim() || newPassword.length < 8) {
      setMessage(t('fpPasswordMin'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage(t('fpPasswordsMismatch'));
      return;
    }
    setIsLoading(true);
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE_URL}/reset_password.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim(), new_password: newPassword })
      });
      const data = await resp.json();
      if (resp.ok && data.status === 'success') {
        toast.success(t('fpResetSuccess'));
        navigate('/login');
      } else {
        toast.error(data.message || t('fpResetFailed'));
        setMessage(data.message || t('fpResetFailed'));
      }
    } catch (err) {
      console.error('Reset error:', err);
      toast.error(t('fpResetFailed'));
      setMessage(t('fpResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {step === 'request' ? t('fpTitleRequest') : t('fpTitleVerify')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {step === 'request' ? t('fpSubtitleRequest') : t('fpSubtitleVerify')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {step === 'request' ? (
            <form className="space-y-6" onSubmit={requestReset}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('emailAddress')}</label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {message && (
                <div className="rounded-md bg-blue-50 p-4">
                  <p className="text-sm text-blue-700">{message}</p>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isLoading ? 'bg-teal-400' : 'bg-teal-600 hover:bg-teal-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500`}
                >
                  {isLoading ? t('fpSending') : t('fpSendCode')}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={resetPassword}>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('emailLabel')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('fpVerificationCode')}</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                  placeholder={t('fpCodePlaceholder')}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('fpNewPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                  placeholder={t('fpPasswordPlaceholder')}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('fpConfirmPassword')}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                  disabled={isLoading}
                />
              </div>

              {message && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-700">{message}</p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep('request')}
                  className="w-1/2 flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  disabled={isLoading}
                >
                  {t('back')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-1/2 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isLoading ? 'bg-teal-400' : 'bg-teal-600 hover:bg-teal-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500`}
                >
                  {isLoading ? t('fpResetting') : t('fpResetPassword')}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('fpRememberPassword')}</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                {t('fpBackToSignIn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
